import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Home, DollarSign, BarChart3, Target, BookOpen, Users, Briefcase, Receipt } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { useLanguage } from '@/contexts/LanguageContext';
import HeaderCore from '@/components/header/HeaderCore';

const navLinksConfig = [
  { nameKey: 'nav.dashboard', path: '/dashboard', icon: Home, requiresAuth: true },
  { nameKey: 'nav.creditReport', path: '/credit-report', icon: BarChart3, requiresAuth: true },
  { nameKey: 'nav.debtTracker', path: '/debt-tracker', icon: DollarSign, requiresAuth: true },
  { nameKey: 'nav.spendingTracker', path: '/spending-tracker', icon: Receipt, requiresAuth: true },
  { nameKey: 'nav.goals', path: '/goals', icon: Target, requiresAuth: true },
  { nameKey: 'nav.netWorth', path: '/net-worth', icon: Briefcase, requiresAuth: true },
  { nameKey: 'nav.learnHub', path: '/learn', icon: BookOpen, requiresAuth: false },
  { nameKey: 'nav.mentors', path: '/mentors', icon: Users, requiresAuth: false },
];

const Header = () => {
  const { user } = useAuth();
  const { t, language, isReady: isLanguageReady } = useLanguage();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const { settings, isLoading: isLoadingSiteSettings } = useSiteSettings();
  
  const appName = useMemo(() => {
    if (!isLanguageReady || isLoadingSiteSettings) return 'UrbanMint'; 
    return settings?.site_title || t('site.title');
  }, [settings, isLoadingSiteSettings, t, language, isLanguageReady]);

  const logoUrl = useMemo(() => {
    if (isLoadingSiteSettings) return '/logo_placeholder.svg'; 
    return settings?.site_logo_url || '/logo_placeholder.svg'; 
  }, [settings, isLoadingSiteSettings]);

  const filteredNavLinks = useMemo(() => {
    if (!isLanguageReady) return [];
    return navLinksConfig
      .filter(link => !link.requiresAuth || (link.requiresAuth && user))
      .map(link => ({ ...link, name: t(link.nameKey) }));
  }, [user, t, language, isLanguageReady]);

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ type: 'spring', stiffness: 70, damping: 20, duration: 0.5 }}
      className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60"
    >
      <HeaderCore
        appName={appName}
        logoUrl={logoUrl}
        filteredNavLinks={filteredNavLinks}
        user={user}
        t={t}
        isSheetOpen={isSheetOpen}
        setIsSheetOpen={setIsSheetOpen}
        isLanguageReady={isLanguageReady}
      />
    </motion.header>
  );
};

export default Header;
  